/**
 * Activity Tracker - Tracks API calls and user actions for bug reporting
 */

export interface ApiCallRecord {
  timestamp: string;
  endpoint: string;
  method: string;
  requestData?: any;
  responseData?: any;
  status?: number;
  error?: string;
  duration?: number;
}

export interface UserActionRecord {
  timestamp: string;
  action: string;
  target?: string;
  details?: any;
}

const MAX_API_CALLS = 20;
const MAX_USER_ACTIONS = 20;

class ActivityTracker {
  private apiCalls: ApiCallRecord[] = [];
  private userActions: UserActionRecord[] = [];

  /**
   * Track an API call
   */
  trackApiCall(record: ApiCallRecord): void {
    this.apiCalls.push(record);

    // Keep only last MAX_API_CALLS
    if (this.apiCalls.length > MAX_API_CALLS) {
      this.apiCalls.shift();
    }
  }

  /**
   * Track a user action
   */
  trackUserAction(action: string, target?: string, details?: any): void {
    const record: UserActionRecord = {
      timestamp: new Date().toISOString(),
      action,
      target,
      details,
    };

    this.userActions.push(record);

    // Keep only last MAX_USER_ACTIONS
    if (this.userActions.length > MAX_USER_ACTIONS) {
      this.userActions.shift();
    }
  }

  /**
   * Get all tracked API calls
   */
  getApiCalls(): ApiCallRecord[] {
    return [...this.apiCalls];
  }

  /**
   * Get all tracked user actions
   */
  getUserActions(): UserActionRecord[] {
    return [...this.userActions];
  }

  /**
   * Get all activity data for bug reports
   */
  getActivitySnapshot() {
    return {
      apiCalls: this.getApiCalls(),
      userActions: this.getUserActions(),
    };
  }

  /**
   * Clear all tracked data
   */
  clear(): void {
    this.apiCalls = [];
    this.userActions = [];
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker();

/**
 * Auto-track DOM events for user actions
 */
export function initializeUserActionTracking(): void {
  // Track button clicks
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as HTMLElement;

      // Find the nearest button or clickable element
      const button = target.closest("button, a, [role='button']");

      if (button) {
        const text = button.textContent?.trim().slice(0, 50) || "Unknown";
        const tag = button.tagName.toLowerCase();
        const classes = Array.from(button.classList).join(" ");

        activityTracker.trackUserAction("click", text, {
          tag,
          classes,
          href: button instanceof HTMLAnchorElement ? button.href : undefined,
        });
      }
    },
    { capture: true }
  );

  // Track navigation
  const trackNavigation = () => {
    activityTracker.trackUserAction("navigation", window.location.href, {
      pathname: window.location.pathname,
      search: window.location.search,
    });
  };

  // Track initial page load
  trackNavigation();

  // Track history changes (for SPAs)
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    trackNavigation();
  };

  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    trackNavigation();
  };

  window.addEventListener("popstate", trackNavigation);

  // Track form submissions
  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target as HTMLFormElement;
      const formId = form.id || form.name || "unnamed-form";

      activityTracker.trackUserAction("form-submit", formId, {
        action: form.action,
        method: form.method,
      });
    },
    { capture: true }
  );

  // Track input focus (to see what user was interacting with)
  let lastFocusedInput: string | null = null;
  document.addEventListener(
    "focus",
    (event) => {
      const target = event.target as HTMLElement;

      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT") {
        const label =
          (target as HTMLInputElement).placeholder ||
          (target as HTMLInputElement).name ||
          (target as HTMLInputElement).id ||
          "unnamed-input";

        // Only track if different from last focused input (reduce noise)
        if (label !== lastFocusedInput) {
          activityTracker.trackUserAction("input-focus", label, {
            type: (target as HTMLInputElement).type,
            tag: target.tagName.toLowerCase(),
          });
          lastFocusedInput = label;
        }
      }
    },
    { capture: true }
  );
}
